/**
 * Per-route <head> metadata for every public route that is NOT backed by MDX.
 *
 * Why this file exists: index.html ships one static <head>, and Vercel's SPA rewrite hands that
 * same file to every route it has no static file for. So before this existed, a crawler fetching
 * /about received the landing page's title, description AND `<link rel="canonical" href="/">` —
 * a canonical pointing at a different page, on a URL we submit in the sitemap. useSeo() corrected
 * it after the render pass, but the raw response said "this page is really the home page".
 *
 * The prerender step (scripts/prerender/run.mjs) reads this map to stamp the correct head onto a
 * static file per route, and the page components read it through useStaticPageSeo() — one source,
 * so the prerendered head and the client-rendered head cannot drift apart.
 *
 * MDX routes are NOT here; their metadata comes from frontmatter via the content registry.
 */

export interface StaticPageSeo {
  /** BRAND_SUFFIX is appended by useSeo/the prerender step unless already present. */
  title: string
  /** meta[name=description]. Aim for 150-160 characters. */
  description: string
  /** Emits `noindex, follow` instead of `index, follow`. Must also be absent from the sitemap. */
  noindex?: boolean
  /**
   * Render real markup at build time, not just a corrected <head>.
   *
   * Only for components in the prerender import graph — marketing pages, which reach `window`
   * exclusively from effects. The auth and billing pages pull in the auth store and API clients,
   * which touch localStorage at module scope and throw in Node, so they get head-only files.
   */
  prerender?: boolean
}

const PAGES = {
  '/': {
    title: 'Cleaning Business Software — Quote, Schedule & Get Paid',
    description:
      'All-in-one software for cleaning businesses. Send quotes, schedule recurring cleans, take online bookings, and get paid by card or ACH. Starting at $29/mo.',
    prerender: true,
  },
  '/about': {
    title: 'About CleanDock',
    description:
      'CleanDock is all-in-one software for cleaning businesses — quotes, recurring scheduling, online booking, job photo proof, and card/ACH payments in one app.',
    prerender: true,
  },
  '/privacy': {
    title: 'Privacy Policy',
    description:
      'How CleanDock collects, uses, and protects data for cleaning businesses and their customers, including contact, job photo, and payment information.',
    prerender: true,
  },
  '/terms': {
    title: 'Terms of Service',
    description:
      'The terms governing your use of CleanDock, the all-in-one quoting, scheduling, online booking, and invoicing app built for cleaning businesses.',
    prerender: true,
  },
  '/email-policy': {
    title: 'Email Communication Policy',
    description:
      'How CleanDock sends quote, invoice, booking, and account email — what we send, why we send it, and how to unsubscribe from non-transactional messages.',
    prerender: true,
  },
  '/sms-consent': {
    title: 'SMS Notifications Consent',
    description:
      'CleanDock SMS program details: the transactional texts we send about bookings and jobs, message frequency, message and data rates, and how to opt out.',
    prerender: true,
  },

  // Head-only below: indexable, but the markup comes from the client render.
  '/auth/login': {
    title: 'Log In',
    description:
      'Log in to CleanDock to manage quotes, schedules, online bookings, job logs, and invoices for your cleaning business.',
  },
  '/auth/signup': {
    title: 'Start Your Free Trial',
    description:
      'Start a 14-day free trial of CleanDock. Quoting, recurring scheduling, online booking, and card/ACH payments for cleaning businesses from $29.99/mo.',
  },

  // Head-only and noindex: gated, transactional, or token-bearing. robots.txt deliberately
  // leaves these crawlable so the noindex is actually readable — a disallowed URL can still be
  // indexed URL-only if something links to it.
  '/auth/tester': {
    title: 'Beta Tester Signup',
    description: 'Invite-only beta tester signup for CleanDock.',
    noindex: true,
  },
  '/auth/signup/complete': {
    title: 'Finish Setting Up Your Account',
    description: 'Complete your CleanDock account setup.',
    noindex: true,
  },
  '/auth/reset-password': {
    title: 'Reset Your Password',
    description: 'Set a new password for your CleanDock account.',
    noindex: true,
  },
  '/billing/success': {
    title: 'Checkout Complete',
    description: 'Your CleanDock subscription is being activated.',
    noindex: true,
  },
  '/billing/cancelled': {
    title: 'Checkout Cancelled',
    description: 'No charges were made to your CleanDock account.',
    noindex: true,
  },
} satisfies Record<string, StaticPageSeo>

export type StaticPagePath = keyof typeof PAGES

export const STATIC_PAGE_SEO: Record<StaticPagePath, StaticPageSeo> = PAGES

/** Widened accessor: callers read `.noindex`/`.prerender` without narrowing per literal key. */
export function getStaticPageSeo(path: StaticPagePath): StaticPageSeo {
  return STATIC_PAGE_SEO[path]
}
